import Grid from "@mui/material/Grid";
import { useTranslation } from 'react-i18next';
import Typography from "@mui/material/Typography";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import JobDetailsVM from "../viewmodel/JobDetailsVM";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import CDropDown2 from "../../../../basecomp/components/CDropDown2";
import CInput2 from "../../../../basecomp/components/CInput2";
import CDateTimePicker from "../../../../basecomp/components/CDateTimePicker";
import CText from "../../../../basecomp/components/CText";

const styles = {
  table: {
    border: "1px solid #d3d3d3",
    borderCollapse: "collapse",
  },
  tableCell: {
    textAlign: "center",
  },
};

const JobDetails = (props) => {
  const {
    spDetails,
    jobDirective,
    equipmentType,
    equipmentID,
    section,
    setSection,
    jobType,
    setJobType,
    resources,
    setResources,
    workDesc,
    setWorkDesc,
    jobCardNumber,
    externalRef,
    startDate,
    finishDate,
    setStartDate,
    setFinishDate,
    handleToastOpen,
  } = props;
  const {
    jobDropdown,
    handleStartDateChange,
    handleFinishDateChange,
    handleIconButtonClick,
    handleButtonDownload,
    isLoading,
  } = JobDetailsVM({
    spDetails,
    jobDirective,
    equipmentType,
    equipmentID,
    jobCardNumber,
    externalRef,
    jobType,
    section,
    resources,
    workDesc,
    startDate,
    finishDate,
    setStartDate,
    setFinishDate,
    handleToastOpen,
  });

  const { t } = useTranslation();

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        sx={{
          margin: {
            xs: 0,
            sm: 0,
            md: '1.6em .8em'
          }
        }}
      >
        <CText cVariant="header_card" cText={t('material_list')} />
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        sx={{
          margin: {
            xs: 0,
            sm: 0,
            md: 2
          }
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={2.2}>
            <Typography style={{ marginBottom: "0.5em" }} noWrap>
              {t('job_type')}
            </Typography>
            <CDropDown2
              cId="sb_job_type"
              cHelperText={t('choose')}
              cData={jobDropdown && jobDropdown.type ? jobDropdown.type : []}
              cErrorMsg=""
              cOnChange={(newValue) => setJobType(newValue)}
              cIsSession={true}
              getOptionLabel={(option) => option || ""}
              isOptionEqualToValue={(option, value) =>
                option === value || option === ""
              }
            />
          </Grid>

          <Grid item xs={12} sm={2.2}>
            <Typography style={{ marginBottom: "0.5em" }} noWrap>
              {t('section')}
            </Typography>

            <CDropDown2
              cId="sb_section"
              cHelperText={t('choose')}
              cData={
                jobDropdown && jobDropdown.section ? jobDropdown.section : []
              }
              cErrorMsg=""
              cOnChange={(newValue) => setSection(newValue)}
              cIsSession={true}
              getOptionLabel={(option) => option || ""}
              isOptionEqualToValue={(option, value) =>
                option === value || option === ""
              }
            />
          </Grid>

          <Grid item xs={12} sm={1.6}>
            <Typography style={{ marginBottom: "0.5em" }} noWrap>
              {t('resources')}
            </Typography>
            <CInput2
              cId="sb_resources"
              cType="number"
              cFullWidth={true}
              cIsSession={true}
              cIsSmall
              cIsRequired
              cValue={resources}
              cHtmlProps={{ min: 0 }}
              cOnChange={(value) => {
                if (value === "") {
                  setResources("");
                  return;
                }

                const parsedValue = parseInt(value, 10);

                if (isNaN(parsedValue)) return;

                setResources(parsedValue < 0 ? 0 : parsedValue);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography style={{ marginBottom: "0.5em" }} noWrap>
              {t('start_date')}
            </Typography>
            <CDateTimePicker
              cId="start_date"
              cOnChange={handleStartDateChange}
              cIsSession={true}
              value={startDate}
              cPlaceholder={t('date_time_format')}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography style={{ marginBottom: "0.5em" }} noWrap>
              {t('end_date')}
            </Typography>
            <CDateTimePicker
              cId="end_date"
              cOnChange={handleFinishDateChange}
              cIsSession={true}
              value={finishDate}
              cPlaceholder={t('date_time_format')}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={12} paddingTop={1}>
          <Typography style={{ marginBottom: "0.5em" }}>
            {t('job_description')}
          </Typography>
          <CInput2
            cId="sb_work_desc"
            cValue={workDesc || ""}
            cOnChange={(value) => setWorkDesc(value)}
            cIsSession={true}
            cFullWidth={true}
            cRows={5}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={12} paddingTop={0.4}>
          <div style={{ overflowX: "auto" }}>
            {spDetails && spDetails.length > 0 ? (
              <Table id="jobdetails">
                <TableHead
                  sx={{
                    backgroundColor: "#052660",
                    "& .MuiTableRow-root": {
                      height: "48px", // Custom header row height
                    },
                    "& .MuiTableCell-root": {
                      padding: "0 16px", // Minimal padding for header cells
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ color: "#fff", textAlign: "left" }}>
                      {t('location_bin')}
                    </TableCell>
                    <TableCell sx={{ color: "#fff", textAlign: "left" }}>
                      {t('material')}
                    </TableCell>
                    <TableCell sx={{ color: "#fff", textAlign: "left" }}>
                      {t('description')}
                    </TableCell>
                    <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                      {t('qty')}
                    </TableCell>
                    <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                      {t('uom')}
                    </TableCell>
                    <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                      {t('ohq')}
                    </TableCell>
                    <TableCell sx={{ color: "#fff", textAlign: "center" }}>
                      {t('qr_code')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    "& .MuiTableRow-root": {
                      height: "45px",
                    },
                    "& .MuiTableCell-root": {
                      padding: "2px 16px",
                    },
                  }}
                >
                  {spDetails
                    .filter((data) => data.material_no)
                    .map((data) => (
                      <TableRow key={data.id}>
                        <TableCell sx={{ textAlign: "left" }}>
                          {data.storage_bin !== "null" ? data.storage_bin : ""}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left" }}>
                          {data.material_no}
                        </TableCell>
                        <TableCell sx={{ textAlign: "left" }}>
                          {data.material_desc}
                        </TableCell>
                        <TableCell sx={styles.tableCell}>
                          {data.quantity}
                        </TableCell>
                        <TableCell sx={styles.tableCell} l>
                          {data.uom}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.tableCell,
                            input: { width: "20px", height: "20px" },
                          }}
                        >
                          <input
                            type="checkbox"
                            defaultChecked={data.status === 1}
                            disabled
                          />
                        </TableCell>

                        <TableCell sx={styles.tableCell}>
                          <QRCodeSVG value={data.material_no} size={40} />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            ) : null}
          </div>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={11.9}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "1.5em",
            gap: "1em",
          }}
        >
          <Tooltip title={t('email_job_details')}>
            <IconButton
              id="email_job_details"
              style={{
                backgroundColor: "#052660",
                color: "#FFFFFF",
                borderRadius: 5,
                padding: "10px",
           
              }}
              onClick={handleIconButtonClick}
            >
              <MailOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('download_job_details')}>
            <IconButton
              id="download_job_details"
              style={{
                backgroundColor: "#052660",
                color: "#FFFFFF",
                borderRadius: 5,
                padding: "10px",
           
              }}
              onClick={handleButtonDownload}
            >
              <CloudDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};

export default JobDetails;
